// MarginPanel — live margin display for the offer builder (Blueprint §4.3.3 / D-5).
//
// Pure presentational. Mirrors functions/src/bccPhase1Operations.computeMargin so
// the operator sees the same number the server will gate on (C-1). Cost inputs
// (standard price, blended AI cost/credit) come from platformConfig — pass them in.

export interface MarginInputs {
  standardPriceAed: number;
  discountPercent: number;
  monthlyIncludedCredits: number;
  initialCreditGrant: number;
  blendedAiCostPerCreditAed: number;
}

export function computeMargin(p: MarginInputs) {
  const discount = Math.min(Math.max(p.discountPercent, 0), 100);
  const finalMonthlyPriceAed = Number((p.standardPriceAed * (1 - discount / 100)).toFixed(2));
  const estimatedCredits = Math.max(p.monthlyIncludedCredits, 0) + Math.max(p.initialCreditGrant, 0) * 0.15;
  const estimatedMonthlyCostAed = Number((estimatedCredits * p.blendedAiCostPerCreditAed).toFixed(2));
  const grossMarginAed = Number((finalMonthlyPriceAed - estimatedMonthlyCostAed).toFixed(2));
  const grossMarginPercent = finalMonthlyPriceAed > 0 ? Number(((grossMarginAed / finalMonthlyPriceAed) * 100).toFixed(2)) : 0;
  return { finalMonthlyPriceAed, estimatedMonthlyCostAed, grossMarginAed, grossMarginPercent };
}

export type MarginHealth = "healthy" | "warn" | "critical" | "blocked";
export function marginHealth(grossMarginPercent: number): MarginHealth {
  if (grossMarginPercent <= 0) return "blocked";
  if (grossMarginPercent < 10) return "critical";
  if (grossMarginPercent < 30) return "warn";
  return "healthy";
}

const HEALTH_CLASS: Record<MarginHealth, string> = {
  healthy: "text-emerald-700 bg-emerald-50 border-emerald-200",
  warn: "text-amber-700 bg-amber-50 border-amber-200",
  critical: "text-orange-700 bg-orange-50 border-orange-200",
  blocked: "text-red-700 bg-red-50 border-red-200",
};

const AED = (n: number) => `AED ${n.toFixed(2)}`;

export function MarginPanel({ inputs }: { inputs: MarginInputs }) {
  const m = computeMargin(inputs);
  const health = marginHealth(m.grossMarginPercent);
  const discountAed = Number((inputs.standardPriceAed * Math.min(Math.max(inputs.discountPercent, 0), 100) / 100).toFixed(2));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Margin (live)</p>
      <dl className="mt-2 space-y-1 text-sm">
        <Row label="Standard price" value={AED(inputs.standardPriceAed)} />
        <Row label={`Discount (${inputs.discountPercent}%)`} value={`− ${AED(discountAed)}`} />
        <Row label="Final price" value={`${AED(m.finalMonthlyPriceAed)} / mo`} bold />
        <Row label="Est. monthly cost" value={`− ${AED(m.estimatedMonthlyCostAed)}`} />
      </dl>
      <div className={`mt-3 rounded-lg border px-3 py-2 text-sm font-bold ${HEALTH_CLASS[health]}`}>
        Gross margin {AED(m.grossMarginAed)} · {m.grossMarginPercent}%
        <span className="ml-2 font-black uppercase">
          {health === "blocked" ? "⛔ blocked" : health === "critical" ? "reason required" : health === "warn" ? "low" : "healthy"}
        </span>
      </div>
      {health === "blocked" ? <p className="mt-1 text-[11px] font-semibold text-red-600">Offer cannot be sent at non-positive margin.</p> : null}
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className={bold ? "font-bold text-slate-900" : "text-slate-700"}>{value}</dd>
    </div>
  );
}
